"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_DEPTH = 29;
exports.MAX_INDEX = (1 << (exports.MAX_DEPTH + 1)) - 1;
exports.BISECT_LEFT = ~(5 << 29); // one then zero then 29 ones
exports.BISECT_RIGHT = 3 << (exports.MAX_DEPTH - 1); // two ones followed by 29 zeroes
var RANDOM_OR_MASK = 1 << 30;
var RANDOM_AND_MASK = (1 << 31) - 1;
var MAX_POSITIVE = -1 >>> 1; // max positive signed integer -- 0 followed by 31 ones 
/**
 * Checks if a branch is valid -- as in it has full coverage and no missing t
 * terminal nodes. This could be an expensive operation for large branches, so
 * you probably don't want to run it too often. It's probably good to use this
 * for validating trees when we build them as static files though.
 *
 * @param {TreeIndex} t the tree to validate
 * @param {number} b the branch of the tree to validate
 * @returns {boolean} if the branch is valid
 */
var validateBranch = function (t, b) {
    // pass if the branch is a node in the tree
    if (t[b]) {
        return true;
    }
    // fail if we've exceeded the max depth
    if (b > exports.MAX_INDEX) {
        return false;
    }
    // check both of the sub branches recursively
    return (validateBranch(t, b * 2) && validateBranch(t, b * 2 + 1));
};
exports.validateTree = function (t) {
    // check if the root branch is valid
    return validateBranch(t, 1);
};
/**
 * Expands a tree index by replacing each of the expanded branches with
 * the new nodes specified in the tree expansion.
 *
 * @param {TreeIndex} t the tree to expand
 * @param {TreeExpansion} tx an associative list of branches with their
 * expansions
 * @return {TreeIndex} an expanded tree index object
 */
exports.expandTree = function (t, tx) {
    var expanded = __assign({}, t);
    for (var bx in tx) {
        if (!tx.hasOwnProperty(bx)) {
            continue;
        }
        delete expanded[bx];
        for (var b in tx[bx]) {
            if (!tx[bx].hasOwnProperty(b)) {
                continue;
            }
            expanded[b] = tx[bx][b];
        }
    }
    return expanded;
};
exports.maxDepth = function (t, b) {
    if (t[b] || b > exports.MAX_INDEX) {
        return 0;
    }
    return (Math.max(exports.maxDepth(t, b * 2) + 1, exports.maxDepth(t, b * 2 + 1) + 1));
};
exports.depth = function (b) { return Math.floor(Math.log2(b)); };
exports.ancestor = function (t, b) {
    while (b > 0) {
        if (t[b]) {
            return b;
        }
        b = b >> 1;
    }
};
exports.relativeAt = function (t, b, rb) {
    // we can't mod something with 0, so we have to use a minimum depth of 1
    // this will mean treating rb=0 the same as 2 and rb=1 the same as 3.
    var d = Math.max(exports.depth(rb), 1);
    var n = b;
    for (var i = 1; n <= exports.MAX_INDEX && i <= exports.MAX_DEPTH + 1; i++) {
        if (t[n]) {
            return (n);
        }
        var shift = i % d;
        n *= 2;
        n = n + ((rb >> ((d - shift) % d)) & 1);
    }
    return null;
};
exports.bisectRight = function (t, b) {
    return exports.relativeAt(t, b, exports.BISECT_RIGHT);
};
exports.bisectLeft = function (t, b) {
    return exports.relativeAt(t, b, exports.BISECT_LEFT);
};
exports.seededRandom = function (t, b, s) {
    var masked = s & RANDOM_AND_MASK | RANDOM_OR_MASK;
    return exports.relativeAt(t, b, masked);
};
exports.getRandomSeed = function () {
    return Math.random() * Number.MAX_SAFE_INTEGER & RANDOM_AND_MASK | RANDOM_OR_MASK;
};
function branchNodes(t, b) {
    if (t[b]) {
        var r = {};
        r[b] = t[b];
        return r;
    }
    var lb = branchNodes(t, b * 2);
    var rb = branchNodes(t, b * 2 + 1);
    return __assign({}, lb, rb);
}
exports.branchNodes = branchNodes;
var buildBranch = function (t, b, d) {
    var start = b << d; // times 2^d
    var end = start + (1 << (d)); // start plus 2^d
    var core = {};
    for (var i = start; i < end; i++) {
        if (t[i]) {
            core[i] = t[i];
        }
        else if (exports.ancestor(t, i)) {
            var a = exports.ancestor(t, i);
            core[a] = t[a];
        }
        else {
            core[i] = t[exports.relativeAt(t, i, 0)];
        }
    }
    return core;
};
var buildBasket = function (t, b, e, f) {
    var start = b << e; // times 2^e
    var end = start + (1 << e); // start plus 2^(e-1)
    var basket = {};
    for (var i = start; i < end; i++) {
        if (exports.ancestor(t, i)) {
            continue;
        } // we don't need to expand this branch
        var branch = buildBranch(t, i, f);
        basket[i] = branch;
    }
    return basket;
};
/**
 * Splits a full tree index up into a smaller representation of the tree
 * followed by a number of expansion baskets. The return value includes
 * the key `core` with the minimimized representation of the full index, as
 * well as numeric keys indicating branches at which point the tree can
 * be expanded using @see #expandTree. Note, @see TreeExpansion objects
 * should only be applied after the application of any ancestor node
 * expansions.
 *
 * **Note**, this is an initial implementation that is subject to change.
 * In particular, the relationship between depth and frequency and how
 * they are specified has likely room for improvement. Expansion size
 * probably makes more sense, but depth was easier to reason through for
 * a first attempt.
 *
 * @param {TreeIndex} t the `tree` to be split
 * @param {number} e the `eagerness` or depth at which baskets should look
 * forward when expanding branches.
 * @param {number} f the `frequency` with which the tree should be expanded
 * @param {number} s a number indicating `strategy` to use for selecting
 * representative nodes for collapsed branches. The number indicates the
 * relative sub branch of the collapsed branch that should be searched to
 * find a represetative child node. @see #relativeAt
 *
 * @return {SplitTree} a separated and expandable tree
 */
exports.splitTree = function (t, e, f) {
    if (f === void 0) { f = 1; }
    var core = buildBranch(t, 1, e);
    var split = { core: core };
    var maxIndex = Object.keys(t)
        .map(function (x) { return parseInt(x); })
        .reduce(function (p, c) { return Math.max(p, c); });
    var maxBasket = maxIndex >> (e + f - 1); // we should never have to go deeper 
    for (var b = 1; b <= maxBasket; b++) {
        var basket = buildBasket(t, b, e, f);
        if (Object.keys(basket).length > 0) {
            split[b] = basket;
        }
        if (!(b & (b + 1))) { // b+1 is an exact power of 2
            /*
            this is a bit convoluted, but we only want to build baskets where
            the branch depth is a multiple of the frequency. If the
            frequency is one, we basically have to build expansions
            for every branch until the last one. For frequency of two,
            we need to build them for branches 1, then 4-7, then skip 16-31, then
            64-127... To do this, we're running a simple loop that increments
            the branch by one every cycle, but when it reaches the end of that
            cycle (depth) it will be one less than an exact power of two,
            so we add one bringing it to that power of two, shift it by the
            frequency minus one (running through the last) set of branches has
            already shifted it by one, and subtract one (which will be added
            back in at the next increment of the for loop). Note, if the frequency
            is 1, this does nothing.
            */
            b = ((b + 1) << (f - 1)) - 1;
        }
    }
    return split;
};
